import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Card, Avatar, Button, Tag, Spin, Modal, Tooltip, message, Timeline } from "antd";
import axios from "axios";
import {
  ArrowLeftOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  CalendarOutlined,
  TeamOutlined,
  VideoCameraOutlined,
  DownloadOutlined,
  CheckCircleFilled,
  LoadingOutlined,
  FilePdfOutlined,
  EyeOutlined,
  FileProtectOutlined,
  LockOutlined,
  StarOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { TalentDetailsWrapper } from "./TalentDetails.style";
import { API_CONST } from "../../../const";
import ScheduleInterviewModal from "../../../components/ScheduleInterviewModal/ScheduleInterviewModal";
import TalentFeedbackModal from "../../../components/TalentFeedbackModal/TalentFeedbackModal";

const formatCurrency = (amount) => {
  if (!amount) return "-";
  return `₹ ${Number(amount).toLocaleString('en-IN')}`;
};

const getFileNameFromUrl = (url) => {
  if (!url) return null;
  const parts = url.split("/");
  return parts[parts.length - 1] || null;
};

const TalentDetails = () => {
  const { talentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [talent, setTalent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [thumbnailUrls, setThumbnailUrls] = useState({});
  const [thumbnailErrors, setThumbnailErrors] = useState({});
  const [loadingDoc, setLoadingDoc] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [isHired, setIsHired] = useState(false);
  const [activeHiringRecordId, setActiveHiringRecordId] = useState(null);
  const [feedbackHistory, setFeedbackHistory] = useState([]);
  const [feedbackHistoryLoading, setFeedbackHistoryLoading] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [downloadingResume, setDownloadingResume] = useState(false);
  const [downloadingBrandedResume, setDownloadingBrandedResume] = useState(false);
  const [scheduleModalVisible, setScheduleModalVisible] = useState(false);
  const [matchedJobs, setMatchedJobs] = useState([]);
  const [matchedJobsCount, setMatchedJobsCount] = useState(0);
  const [matchedJobsLoading, setMatchedJobsLoading] = useState(false);

  const backPath = location.state?.from || "/customer/find-talents";
  const backLabel = location.state?.fromLabel || "Back to Find Talents";

  useEffect(() => {
    const fetchTalentProfile = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");
        const response = await axios.get(`${API_CONST.GET_TALENT_PROFILE}/${talentId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data.profile) {
          const profile = response.data.profile;
          const clientRate = profile.clientRate || profile.rate || profile.monthlyRate || 0;
          setTalent({
            ...profile,
            location: profile.location,
            experience: `${profile.experienceYears || 0} Years${profile.experienceMonths > 0 ? ` ${profile.experienceMonths} Months` : ''}`,
            noticePeriod: profile.notice || profile.noticePeriod || "Not specified",
            monthlyRate: formatCurrency(clientRate),
            hourlyRate: formatCurrency(Math.round(clientRate / 480)),
            marketRate: formatCurrency(Math.round(clientRate * 1.1)),
            organization: profile.partnerOrganization,
            phone: profile.phone,
            skills: profile.skills || [],
            primarySkills: profile.primarySkills || [],
            secondarySkills: profile.secondarySkills || [],
            projects: profile.projects || [],
          });

          const docs = [
            {
              type: "resume",
              typeName: "Resume",
              url: profile.resumeUrl || null,
              fileName: getFileNameFromUrl(profile.resumeUrl),
            },
            {
              type: "aadhaar",
              typeName: "Aadhaar Card",
              url: profile.aadhaarUrl || null,
              fileName: getFileNameFromUrl(profile.aadhaarUrl),
              idNumber: profile.aadhaarNumber || null,
              idLabel: "Aadhaar Number",
            },
            {
              type: "pan",
              typeName: "PAN Card",
              url: profile.panUrl || null,
              fileName: getFileNameFromUrl(profile.panUrl),
              idNumber: profile.panNumber || null,
              idLabel: "PAN Number",
            },
            {
              type: "degree",
              typeName: "Degree Certificate",
              url: profile.degreeUrl || null,
              fileName: getFileNameFromUrl(profile.degreeUrl),
            },
          ];
          setDocuments(docs);
        }
      } catch (error) {
        console.error("Error fetching talent profile:", error);
        message.error("Failed to load talent profile");
      } finally {
        setLoading(false);
      }
    };

    if (talentId) {
      fetchTalentProfile();
    }
  }, [talentId]);

  useEffect(() => {
    if (!talentId) return;
    const fetchJobMatches = async () => {
      setMatchedJobsLoading(true);
      try {
        const token = localStorage.getItem("authToken");
        const res = await axios.get(
          `${API_CONST.TALENT_JOB_MATCHES}/${talentId}/job-matches`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMatchedJobsCount(res.data.matchCount || 0);
        setMatchedJobs(res.data.matchedJobs || []);
      } catch (e) {
        console.error("Error fetching job matches:", e);
      } finally {
        setMatchedJobsLoading(false);
      }
    };
    fetchJobMatches();
  }, [talentId]);

  useEffect(() => {
    if (!talentId) return;
    const checkHiringStatus = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const res = await axios.post(
          API_CONST.GET_HIRING_RECORDS,
          { talentId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const records = res.data.hiringRecords || [];
        const active = records.find(r => r.status === 'active') || records[0];
        setIsHired(records.length > 0);
        setActiveHiringRecordId(active?.id || null);
      } catch {
        setIsHired(false);
      }
    };
    checkHiringStatus();
  }, [talentId]);

  useEffect(() => {
    if (!activeHiringRecordId) return;
    const fetchFeedback = async () => {
      setFeedbackHistoryLoading(true);
      try {
        const token = localStorage.getItem("authToken");
        const res = await axios.get(
          `${API_CONST.PERFORMANCE_FEEDBACK_HIRING_RECORD}/${activeHiringRecordId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setFeedbackHistory(res.data.feedback || []);
      } catch (e) {
        // silent — feedback section is non-critical
      } finally {
        setFeedbackHistoryLoading(false);
      }
    };
    fetchFeedback();
  }, [activeHiringRecordId]);

  useEffect(() => {
    if (!documents.some((d) => d.url)) return;
    const loadThumbnails = async () => {
      const token = localStorage.getItem("authToken");
      const urls = {};
      for (const doc of documents) {
        if (doc.url) {
          try {
            const response = await axios.post(
              API_CONST.GET_DOCUMENT_URL,
              { objectPath: doc.url },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            urls[doc.type] = response.data.downloadUrl;
          } catch (e) {
            // skip silently
          }
        }
      }
      setThumbnailUrls(urls);
    };
    loadThumbnails();
  }, [documents]);

  const getDocumentDownloadUrl = async (objectPath) => {
    const token = localStorage.getItem("authToken");
    const response = await axios.post(
      API_CONST.GET_DOCUMENT_URL,
      { objectPath },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data.downloadUrl;
  };

  const handleView = async (doc) => {
    if (!doc.url) { message.warning("No document uploaded"); return; }
    setLoadingDoc(doc.type);
    try {
      const downloadUrl = await getDocumentDownloadUrl(doc.url);
      setPreviewUrl(downloadUrl);
      setPreviewTitle(doc.typeName);
      setPreviewVisible(true);
    } catch {
      message.error("Failed to load document");
    } finally {
      setLoadingDoc(null);
    }
  };

  const handleDownload = async (doc) => {
    if (!doc.url) { message.warning("No document uploaded"); return; }
    setLoadingDoc(doc.type);
    try {
      const downloadUrl = await getDocumentDownloadUrl(doc.url);
      window.open(downloadUrl, "_blank");
    } catch {
      message.error("Failed to download document");
    } finally {
      setLoadingDoc(null);
    }
  };

  const handleDownloadResume = async () => {
    if (!talent?.resumeUrl) {
      message.warning("No original resume uploaded for this talent");
      return;
    }
    setDownloadingResume(true);
    try {
      const downloadUrl = await getDocumentDownloadUrl(talent.resumeUrl);
      window.open(downloadUrl, "_blank");
    } catch {
      message.error("Failed to download resume");
    } finally {
      setDownloadingResume(false);
    }
  };

  const handleDownloadBrandedResume = async () => {
    setDownloadingBrandedResume(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${API_CONST.BRANDED_RESUME_BASE}/${talentId}/branded-resume`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.downloadUrl) {
        window.open(response.data.downloadUrl, "_blank");
      } else {
        message.error("Could not get resume download URL");
      }
    } catch (error) {
      console.error("Error downloading branded resume:", error);
      message.error("Failed to download HireNXT resume. Please try again.");
    } finally {
      setDownloadingBrandedResume(false);
    }
  };

  const hasDocuments = documents.some((d) => d.url);
  const SENSITIVE_DOC_TYPES = ["aadhaar", "pan", "degree"];
  const sensitiveDocs = documents.filter((d) => d.url && SENSITIVE_DOC_TYPES.includes(d.type));
  const hasSensitiveDocs = sensitiveDocs.length > 0 || documents.some((d) => SENSITIVE_DOC_TYPES.includes(d.type));

  if (loading) {
    return (
      <TalentDetailsWrapper>
        <div style={{ padding: "100px", textAlign: "center" }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 36 }} spin />} />
          <p style={{ marginTop: 16, color: "#666" }}>Loading talent profile...</p>
        </div>
      </TalentDetailsWrapper>
    );
  }

  if (!talent) {
    return (
      <TalentDetailsWrapper>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <h2>Talent not found</h2>
          <Button type="primary" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        </div>
      </TalentDetailsWrapper>
    );
  }

  return (
    <TalentDetailsWrapper>
      <div className="header">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(backPath)}
          className="back-button"
        >
          {backLabel}
        </Button>
      </div>

      <Card className="profile-card">
        <div className="profile-header">
          <div className="avatar-section">
            <Avatar size={100} className="talent-avatar">
              {talent.name?.charAt(0) || '?'}
            </Avatar>
          </div>

          <div className="info-section">
            <h1>{talent.name}</h1>
            <p className="role">{talent.role}</p>

            {(talent.idVerificationStatus || talent.backgroundVerified) && (
              <div className="verification-badges">
                {talent.idVerificationStatus === "Verified" && (
                  <Tag
                    icon={<CheckCircleFilled />}
                    color="success"
                    style={{ fontSize: "13px", padding: "4px 12px", borderRadius: "4px" }}
                  >
                    ID Verified
                  </Tag>
                )}
                {talent.idVerificationStatus === "Failed" && (
                  <Tag
                    color="error"
                    style={{ fontSize: "13px", padding: "4px 12px", borderRadius: "4px" }}
                  >
                    ID Failed
                  </Tag>
                )}
                {talent.idVerificationStatus === "Pending" && (
                  <Tag
                    color="warning"
                    style={{ fontSize: "13px", padding: "4px 12px", borderRadius: "4px" }}
                  >
                    ID Verification Pending
                  </Tag>
                )}
                {talent.backgroundVerified && (
                  <Tag
                    icon={talent.backgroundVerified === "Yes" ? <CheckCircleFilled /> : null}
                    color={talent.backgroundVerified === "Yes" ? "success" : talent.backgroundVerified === "Pending" ? "warning" : "default"}
                    style={{ fontSize: "13px", padding: "4px 12px", borderRadius: "4px" }}
                  >
                    {talent.backgroundVerified === "Yes" ? "Background Verified" :
                     talent.backgroundVerified === "Pending" ? "BG Verification Pending" : "BG Not Verified"}
                  </Tag>
                )}
              </div>
            )}

            <div className="meta-info">
              <div className="info-item">
                <EnvironmentOutlined />
                <span>{talent.location}</span>
              </div>
              <div className="info-item">
                <ClockCircleOutlined />
                <span>{talent.experience} Experience</span>
              </div>
              <div className="info-item">
                <CalendarOutlined />
                <span>{talent.noticePeriod} Notice</span>
              </div>
            </div>
          </div>

          <div className="action-section">
            <Button
              type="primary"
              icon={<VideoCameraOutlined />}
              block
              onClick={() => setScheduleModalVisible(true)}
            >
              Schedule Interview
            </Button>
            <Button
              icon={<DownloadOutlined />}
              block
              loading={downloadingBrandedResume}
              onClick={handleDownloadBrandedResume}
            >
              Download HireNXT Resume
            </Button>
          </div>
        </div>

        <div className="details-row">
          <div className="detail-item">
            <h4>Hourly Rate</h4>
            <p className="highlight">{talent.hourlyRate}</p>
          </div>
          <div className="detail-item">
            <h4>Monthly Rate</h4>
            <p className="highlight">{talent.monthlyRate}</p>
          </div>
          <div className="detail-item">
            <h4>Market Rate</h4>
            <p>{talent.marketRate}</p>
          </div>
          <div className="detail-item">
            <h4>Availability</h4>
            <p>{talent.availability}</p>
          </div>
        </div>
      </Card>

      <Card className="summary-card">
        <h3>Professional Summary</h3>
        <p>{talent.summary || "No summary available"}</p>
      </Card>

      <Card className="skills-card">
        <h3>Primary Skills</h3>
        <div className="skills-grid">
          {talent.primarySkills && talent.primarySkills.length > 0 ? (
            talent.primarySkills.filter(Boolean).map((skill, index) => (
              <Tag key={index} color="cyan" style={{ fontSize: 14, padding: "6px 12px" }}>
                {skill}
              </Tag>
            ))
          ) : (
            <p style={{ color: "#999" }}>No primary skills added</p>
          )}
        </div>
      </Card>

      {talent.secondarySkills && talent.secondarySkills.length > 0 && (
        <Card className="skills-card">
          <h3>Secondary Skills</h3>
          <div className="skills-grid">
            {talent.secondarySkills.filter(Boolean).map((skill, index) => (
              <Tag key={index} color="blue" style={{ fontSize: 14, padding: "6px 12px" }}>
                {skill}
              </Tag>
            ))}
          </div>
        </Card>
      )}

      {isHired && (
        <Card
          style={{ borderRadius: 8, marginTop: 16 }}
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <StarOutlined style={{ color: "#014c75", fontSize: 18 }} />
              <span style={{ fontWeight: 600, color: "#014c75" }}>
                Performance Feedback {feedbackHistory.length > 0 ? `(${feedbackHistory.length})` : ""}
              </span>
            </div>
          }
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="small"
              style={{ background: "#014c75" }}
              onClick={() => setFeedbackModalOpen(true)}
            >
              Give Feedback
            </Button>
          }
        >
          {feedbackHistoryLoading ? (
            <div style={{ textAlign: "center", padding: 32 }}><Spin /></div>
          ) : feedbackHistory.length === 0 ? (
            <div style={{ textAlign: "center", padding: "24px 0", color: "#aaa", fontSize: 13 }}>
              No feedback submitted yet. Use the button above to share your experience.
            </div>
          ) : (
            <Timeline
              style={{ marginTop: 8 }}
              items={feedbackHistory.map(f => {
                const typeColor = f.feedbackType === "instant" ? "orange" : f.feedbackType === "monthly" ? "#1677ff" : "#7c3aed";
                const typeLabel = f.feedbackType === "instant" ? "Instant" : f.feedbackType === "monthly" ? `Monthly (${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][(f.month||1)-1]} ${f.year})` : "Contract End";
                return {
                  color: typeColor,
                  children: (
                    <div style={{ padding: "6px 10px", background: "#f8fbfe", borderRadius: 6, border: "1px solid #e6f0f8" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <div>
                          <Tag color={typeColor}>{typeLabel}</Tag>
                          {f.instantSubType && <Tag>{f.instantSubType.replace("_", " ")}</Tag>}
                          {f.severity && <Tag color={f.severity === "high" ? "red" : "orange"}>{f.severity}</Tag>}
                          <Tag color={f.status === "open" ? "red" : "default"} style={{ marginLeft: 4 }}>
                            {f.status === "open" ? "Open" : f.status === "acknowledged_by_partner" ? "Acknowledged" : "Closed"}
                          </Tag>
                        </div>
                        <span style={{ fontSize: 11, color: "#aaa" }}>
                          {new Date(f.createdAt).toLocaleDateString("en-IN")}
                        </span>
                      </div>
                      {f.title && <div style={{ fontWeight: 600, fontSize: 13, marginTop: 4 }}>{f.title}</div>}
                      {(f.description || f.comments) && (
                        <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>{f.description || f.comments}</div>
                      )}
                      {f.ratingBucket && (
                        <div style={{ marginTop: 4 }}>
                          <Tag color="blue">{f.ratingBucket.replace(/_/g, " ")}</Tag>
                          {f.overallScore !== null && f.overallScore !== undefined && (
                            <strong style={{ color: "#014c75", fontSize: 13 }}>{f.overallScore}%</strong>
                          )}
                        </div>
                      )}
                      {f.responses && f.responses.length > 0 && (
                        <div style={{ marginTop: 6, fontSize: 12, color: "#888" }}>
                          {f.responses.length} partner response{f.responses.length > 1 ? "s" : ""}
                        </div>
                      )}
                    </div>
                  ),
                };
              })}
            />
          )}
        </Card>
      )}

      <TalentFeedbackModal
        open={feedbackModalOpen}
        onClose={() => setFeedbackModalOpen(false)}
        onSuccess={async () => {
          if (!activeHiringRecordId) return;
          const token = localStorage.getItem("authToken");
          try {
            const res = await axios.get(
              `${API_CONST.PERFORMANCE_FEEDBACK_HIRING_RECORD}/${activeHiringRecordId}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            setFeedbackHistory(res.data.feedback || []);
          } catch { }
        }}
        hiringRecordId={activeHiringRecordId}
        talentName={talent?.name}
      />

      {matchedJobsCount > 0 && (
        <Card className="matched-jobs-card" style={{ borderRadius: 8, marginTop: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <CheckCircleFilled style={{ fontSize: 20, color: "#01d9a9" }} />
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "#014c75" }}>
              Matched Jobs ({matchedJobsCount})
            </h3>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {matchedJobs.map((job) => (
              <div
                key={job.id}
                style={{
                  padding: "12px 14px",
                  background: "#f8fbfe",
                  border: "1px solid #e6f0f8",
                  borderRadius: 6,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, color: "#014c75", fontSize: 14 }}>
                    {job.title}
                  </div>
                  <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                    {job.customerOrganization}
                  </div>
                </div>
                <Tag color={job.status === "open" ? "success" : "processing"}>
                  {job.status === "open" ? "Open" : "Pending"}
                </Tag>
              </div>
            ))}
          </div>
        </Card>
      )}

      {talent.projects && talent.projects.length > 0 && (
        <Card className="projects-card">
          <h3>Project Experience</h3>
          {talent.projects.map((project, index) => (
            <div key={index} className="project-item">
              <h4>{project.title}</h4>
              <div className="project-meta">
                {project.client && <span><TeamOutlined /> {project.client}</span>}
                {project.duration && <span><CalendarOutlined /> {project.duration}</span>}
                {project.role && <span><DollarOutlined /> {project.role}</span>}
              </div>
              {project.description && <p>{project.description}</p>}
              {project.technologies && (
                <div className="tech-stack">
                  {(Array.isArray(project.technologies)
                    ? project.technologies
                    : project.technologies.split(',').map(t => t.trim()).filter(Boolean)
                  ).map((tech, idx) => (
                    <Tag key={idx} color="blue">
                      {tech}
                    </Tag>
                  ))}
                </div>
              )}
            </div>
          ))}
        </Card>
      )}

      {hasSensitiveDocs && (
        <Card
          style={{
            background: "white",
            borderRadius: 8,
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            marginTop: 24,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
            <FileProtectOutlined style={{ fontSize: 20, color: "#014c75" }} />
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: "#333" }}>
              Identity Documents
            </h3>
            {!isHired && (
              <Tag icon={<LockOutlined />} color="warning" style={{ marginLeft: "auto" }}>
                Available after hire
              </Tag>
            )}
          </div>

          {!isHired ? (
            <div
              style={{
                textAlign: "center",
                padding: "40px 24px",
                background: "#f8f9fd",
                borderRadius: 8,
                border: "2px dashed #e8e8e8",
              }}
            >
              <LockOutlined style={{ fontSize: 40, color: "#bbb", marginBottom: 16 }} />
              <p style={{ fontSize: 15, color: "#595959", fontWeight: 500, margin: "0 0 8px" }}>
                Identity documents are private
              </p>
              <p style={{ fontSize: 13, color: "#999", margin: 0 }}>
                Aadhaar, PAN, and degree certificates will be accessible once this talent is hired by your organisation.
              </p>
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                gap: 20,
              }}
            >
              {sensitiveDocs.map((doc, idx) => (
                <div
                  key={idx}
                  style={{
                    background: "#f8f9fd",
                    borderRadius: 8,
                    overflow: "hidden",
                    border: "1px solid #e8e8e8",
                  }}
                >
                  <div
                    style={{
                      background: "white",
                      padding: "12px 16px",
                      borderBottom: "1px solid #e8e8e8",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <FilePdfOutlined style={{ fontSize: 18, color: "#014c75" }} />
                      <span style={{ fontWeight: 600, fontSize: 14, color: "#333" }}>{doc.typeName}</span>
                    </div>
                    <Tag icon={<CheckCircleFilled />} color="success" style={{ margin: 0 }}>
                      Available
                    </Tag>
                  </div>

                  <div style={{ padding: 16 }}>
                    <div
                      style={{
                        background: "#fff",
                        border: "2px dashed #e8e8e8",
                        borderRadius: 8,
                        height: 140,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: 14,
                        cursor: "pointer",
                        transition: "all 0.3s",
                        overflow: "hidden",
                      }}
                      onClick={() => handleView(doc)}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#014c75"; e.currentTarget.style.background = "#f0f5ff"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e8e8e8"; e.currentTarget.style.background = "#fff"; }}
                    >
                      {loadingDoc === doc.type ? (
                        <LoadingOutlined style={{ fontSize: 36, color: "#014c75" }} spin />
                      ) : thumbnailUrls[doc.type] && !thumbnailErrors[doc.type] ? (
                        <img
                          src={thumbnailUrls[doc.type]}
                          alt={doc.typeName}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          onError={() => setThumbnailErrors((prev) => ({ ...prev, [doc.type]: true }))}
                        />
                      ) : thumbnailUrls[doc.type] && thumbnailErrors[doc.type] ? (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                          <FilePdfOutlined style={{ fontSize: 36, color: "#ff4d4f", marginBottom: 8 }} />
                          <span style={{ fontSize: 11, fontWeight: 600, color: "#ff4d4f", letterSpacing: 1 }}>PDF</span>
                          <span style={{ fontSize: 12, color: "#999", marginTop: 4 }}>Click to preview</span>
                        </div>
                      ) : (
                        <>
                          <FilePdfOutlined style={{ fontSize: 36, color: "#bbb", marginBottom: 8 }} />
                          <span style={{ fontSize: 12, color: "#999" }}>Click to preview</span>
                        </>
                      )}
                    </div>

                    <div style={{ marginBottom: 12 }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "6px 0",
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        <span style={{ color: "#999", fontSize: 12 }}>File</span>
                        <span
                          style={{ color: "#333", fontSize: 12, fontWeight: 500, maxWidth: 160, textAlign: "right", wordBreak: "break-all" }}
                          title={doc.fileName}
                        >
                          {doc.fileName
                            ? doc.fileName.length > 22
                              ? doc.fileName.substring(0, 22) + "..."
                              : doc.fileName
                            : "-"}
                        </span>
                      </div>
                      {doc.idLabel && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            padding: "6px 0",
                          }}
                        >
                          <span style={{ color: "#999", fontSize: 12 }}>{doc.idLabel}</span>
                          <Tooltip title={!doc.idNumber ? "Number not yet extracted" : ""}>
                            <span
                              style={{
                                color: doc.idNumber ? "#014c75" : "#ccc",
                                fontSize: 12,
                                fontWeight: doc.idNumber ? 600 : 400,
                                fontFamily: doc.idNumber ? "monospace" : "inherit",
                                letterSpacing: doc.idNumber ? "0.5px" : "normal",
                              }}
                            >
                              {doc.idNumber || "Not extracted"}
                            </span>
                          </Tooltip>
                        </div>
                      )}
                    </div>

                    <div style={{ display: "flex", gap: 8 }}>
                      <Button
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleView(doc)}
                        loading={loadingDoc === doc.type}
                        style={{ flex: 1 }}
                      >
                        View
                      </Button>
                      <Button
                        size="small"
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={() => handleDownload(doc)}
                        loading={loadingDoc === doc.type}
                        style={{ flex: 1, background: "#00d9a9", borderColor: "#00d9a9" }}
                      >
                        Download
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      <Modal
        title={previewTitle}
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
        width={900}
        centered
      >
        <iframe
          src={previewUrl}
          style={{ width: "100%", height: "70vh", border: "none" }}
          title={previewTitle}
        />
      </Modal>

      <ScheduleInterviewModal
        visible={scheduleModalVisible}
        talentId={talentId}
        talentName={talent?.name}
        onClose={() => setScheduleModalVisible(false)}
      />
    </TalentDetailsWrapper>
  );
};

export default TalentDetails;
